import React, { useEffect, useState, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { Plus as IcPlus, Trash2 as IcTrash, Check as IcCheck, X as IcX } from 'lucide-react';
import ReplyEditor from '../components/ReplyEditor';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';
const CRIMSON = '#8B1A1A';

const blankDraft = { id: null, shortcut: '', title: '', body_html: '' };

export default function Snippets() {
  const ctx = useOutletContext() || {};
  const { clientId, isGlobalMode } = ctx;
  const { getToken } = useAuth();

  const [snippets, setSnippets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [draft, setDraft] = useState(null); // null = no editor open
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const fetchSnippets = useCallback(async () => {
    if (!clientId) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/clients/${clientId}/snippets`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      const data = await res.json();
      setSnippets(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [clientId, getToken]);

  useEffect(() => { fetchSnippets(); }, [fetchSnippets]);

  const openNew = () => { setDraft({ ...blankDraft }); setSaved(false); };
  const openEdit = (s) => {
    setDraft({ id: s.id, shortcut: s.shortcut || '', title: s.title || '', body_html: s.body_html || s.body_text || '' });
    setSaved(false);
  };
  const closeEditor = () => { setDraft(null); setSaved(false); };

  const save = useCallback(async () => {
    if (!draft) return;
    if (!draft.title.trim() || !(draft.body_html || '').trim()) {
      setError('Title and body are both required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const token = await getToken();
      const cleanHtml = (draft.body_html === '<p></p>') ? '' : draft.body_html;
      const tmp = document.createElement('div');
      tmp.innerHTML = cleanHtml;
      const bodyText = tmp.textContent || '';
      const payload = {
        shortcut: draft.shortcut.trim() || null,
        title: draft.title.trim(),
        body_html: cleanHtml,
        body_text: bodyText,
      };
      let res;
      if (draft.id) {
        res = await fetch(`${API_BASE}/snippets/${draft.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(payload),
        });
      } else {
        res = await fetch(`${API_BASE}/clients/${clientId}/snippets`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(payload),
        });
      }
      if (!res.ok) throw new Error(`Save failed (${res.status})`);
      setSaved(true);
      await fetchSnippets();
      setDraft(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }, [draft, clientId, getToken, fetchSnippets]);

  const remove = useCallback(async (s) => {
    if (!window.confirm(`Delete snippet "${s.title}"? This cannot be undone.`)) return;
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/snippets/${s.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      if (draft?.id === s.id) setDraft(null);
      await fetchSnippets();
    } catch (e) {
      setError(e.message);
    }
  }, [getToken, fetchSnippets, draft]);

  const filtered = snippets.filter((s) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (s.title || '').toLowerCase().includes(q)
      || (s.shortcut || '').toLowerCase().includes(q)
      || (s.body_text || '').toLowerCase().includes(q);
  });

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, color: '#111' }}>Snippets</h1>
          <p style={{ color: '#666', marginTop: 6, marginBottom: 20, fontSize: 14 }}>
            Reusable blocks of text you can drop into replies.
          </p>
        </div>
        {clientId && !draft && (
          <button
            type="button"
            onClick={openNew}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 16px', border: 'none', borderRadius: 8, cursor: 'pointer', background: CRIMSON, color: '#fff', fontSize: 14, fontWeight: 600 }}
          >
            <IcPlus size={16} /> New snippet
          </button>
        )}
      </div>

      {error && (
        <div style={{ background: '#FDEDED', color: '#b91c1c', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 14 }}>
          {error}
        </div>
      )}

      {isGlobalMode || !clientId ? (
        <div style={{ padding: '60px 20px', textAlign: 'center', color: '#999', border: '1px dashed #DDD', borderRadius: 12 }}>
          Select a client workspace to manage its snippets.
        </div>
      ) : draft ? (
        <div style={{ border: '1px solid #EEE', borderRadius: 12, padding: 20, maxWidth: 900 }}>
          <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>Title</label>
              <input
                value={draft.title}
                onChange={(e) => { setDraft({ ...draft, title: e.target.value }); setSaved(false); }}
                placeholder="e.g. Meeting follow-up"
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #E2E2E2', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ width: 200 }}>
              <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>Shortcut (optional)</label>
              <input
                value={draft.shortcut}
                onChange={(e) => { setDraft({ ...draft, shortcut: e.target.value }); setSaved(false); }}
                placeholder="e.g. /followup"
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #E2E2E2', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
              />
            </div>
          </div>

          <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>Body</label>
          <ReplyEditor
            value={draft.body_html}
            onChange={(html) => { setDraft({ ...draft, body_html: html }); setSaved(false); }}
            placeholder="Compose the snippet content…"
            clientId={clientId}
          />

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16 }}>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              style={{ padding: '9px 18px', border: 'none', borderRadius: 8, cursor: saving ? 'default' : 'pointer', background: CRIMSON, color: '#fff', fontSize: 14, fontWeight: 600, opacity: saving ? 0.6 : 1 }}
            >
              {saving ? 'Saving…' : (draft.id ? 'Save changes' : 'Create snippet')}
            </button>
            <button
              type="button"
              onClick={closeEditor}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '9px 14px', border: '1px solid #E2E2E2', borderRadius: 8, cursor: 'pointer', background: '#fff', color: '#444', fontSize: 14 }}
            >
              <IcX size={15} /> Cancel
            </button>
            {saved && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: '#1a7f3c', fontSize: 14 }}>
                <IcCheck size={16} /> Saved
              </span>
            )}
          </div>
        </div>
      ) : (
        <div style={{ maxWidth: 900 }}>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search snippets…"
            style={{ width: '100%', padding: '8px 12px', border: '1px solid #E2E2E2', borderRadius: 8, fontSize: 14, marginBottom: 14, boxSizing: 'border-box' }}
          />
          {loading ? (
            <p style={{ color: '#999', fontSize: 14 }}>Loading snippets…</p>
          ) : filtered.length === 0 ? (
            <div style={{ padding: '60px 20px', textAlign: 'center', color: '#999', border: '1px dashed #DDD', borderRadius: 12 }}>
              {snippets.length === 0 ? 'No snippets yet. Create your first one.' : 'No snippets match your search.'}
            </div>
          ) : (
            <div style={{ border: '1px solid #EEE', borderRadius: 12, overflow: 'hidden' }}>
              {filtered.map((s) => (
                <div
                  key={s.id}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderBottom: '1px solid #F2F2F2' }}
                >
                  <button
                    type="button"
                    onClick={() => openEdit(s)}
                    style={{ flex: 1, textAlign: 'left', border: 'none', background: 'transparent', cursor: 'pointer', padding: 0 }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 14, fontWeight: 600, color: '#222' }}>{s.title}</span>
                      {s.shortcut && (
                        <span style={{ fontSize: 12, color: CRIMSON, background: '#F1ECEC', padding: '1px 7px', borderRadius: 5, fontFamily: 'ui-monospace, monospace' }}>{s.shortcut}</span>
                      )}
                    </div>
                    <div style={{ fontSize: 13, color: '#888', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 560 }}>
                      {s.body_text || '—'}
                    </div>
                  </button>
                  <span style={{ fontSize: 12, color: '#aaa', whiteSpace: 'nowrap' }}>
                    used {s.usage_count || 0}×
                  </span>
                  <button
                    type="button"
                    onClick={() => remove(s)}
                    title="Delete snippet"
                    style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, border: 'none', borderRadius: 7, cursor: 'pointer', background: 'transparent', color: '#b91c1c' }}
                  >
                    <IcTrash size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
